<template>
  <v-app>
    <v-main ref='mainPlaylist'>
      <LoadingSpinner v-if="!isLoaded"/>
      <router-view v-if="isLoaded"/>
      <v-snackbar
        top
        right
        class="pt-16 pb-0"
        role="alert"
        v-model="snackbar.open"
        :timeout="snackbar.timeout"
        :color="snackbar.color"
      >
        <v-row
          class="align-center">
          <v-col
            class="body-1">
            {{ snackbar.text }}
          </v-col>
          <v-col
            v-if="snackbar.action"
            class="text-right">
            <v-btn
            class="d-inline-flex"
            ref="snackbarButton"
            text
            @click="handleLoadNextItem"
            >
              Next
            </v-btn>
          </v-col>
        </v-row>
      </v-snackbar>
    </v-main>
    <PlaypositOffline :readyToInject="isLoaded"/>
    <CreateLmbModal />
  </v-app>
</template>

<script>
/* global dataLayer, drift */
import {
  mapState,
  mapActions,
  mapMutations,
  mapGetters,
} from 'vuex';
import _ from 'lodash';
import LoadingSpinner from './components/LoadingSpinner.vue';
import CreateLmbModal from './components/CreateLmbModal.vue';

const store = require('store');

export default {
  name: 'Playlist',
  components: {
    LoadingSpinner,
    CreateLmbModal,
  },
  data: () => ({
    resetBulb: null,
    snackbarAction: false,
  }),
  computed: {
    ...mapState([
      'activeModuleItemId',
      'activePlaylistVideo',
      'allBulbsInPlaylist',
      'allModuleItemAttempts',
      'courseCopy',
      'courseHash',
      'editMode',
      'isCompleted',
      'nextItemToastTimeout',
      'playlist',
      'playlistAttempt',
      'playlistHasLMBs',
      'playlistId',
      'isLoaded',
      'isLti',
      'requiredItemIndex',
      'sectionId',
      'showSlideout',
      'user',
      'userToken',
    ]),
    ...mapGetters([
      'activeModuleItemAttempt',
      'lmsConsumerVersion',
      'queryParams',
      'snackbar',
    ]),
  },
  methods: {
    ...mapActions([
      'getOrCreateLMB',
      'getPlaylist',
      'markModuleItemAttemptComplete',
      'setModuleItem',
    ]),
    ...mapMutations([
      'closeSnackbar',
      'setAllModuleItemAttempts',
      'setActiveLMB',
      'setActiveModuleItemId',
      'setActivePlaylistVideo',
      'setActiveResourceLinkURL',
      'setActiveTextElement',
      'setAllBulbsInPlaylist',
      'setDownloadResourceURL',
      'setIsCompleted',
      'setIsLoaded',
      'setIsLti',
      'setLti13Launch',
      'setLmsConsumerVersion',
      'setLmsIssuer',
      'setNextItemTimeoutToast',
      'setNsf',
      'setPlaylist',
      'setPlaylistAttempt',
      'setPlaylistHasLMBs',
      'setPlaylistId',
      'setQueryParams',
      'setResourceLinkId',
      'setRestrictPlaylistAccess',
      'setSectionId',
      'setStudentSubmissionId',
      'setUserToken',
      'toggleSnackbar',
    ]),
    async playerPostMessageReceived(e) {
      try {
        // Adobe sends a LOT of postMessages - we should not treat these as errors
        if (
          e.origin === 'https://documentcloud.adobe.com'
          || _.get(e, 'data.source', '').includes('vue')
        ) return;
        // Check to make sure postMessage has e.data
        if (
          _.get(e, 'data')
          && typeof e.data === 'string'
          && (
            e.data.includes('playposit__completion')
            || e.data.includes('playposit__playlist_bulbRetry')
          )
        ) {
          const message = JSON.parse(e.data);
          // Check to make sure postMessage from iFrame has completed message
          if (message.type === 'playposit__completion') {
            const lastModuleItemIndex = this.playlist.allModuleItems.length - 1;
            const currentModuleItemIndex = _.findIndex(this.playlist.allModuleItems, { itemId: this.activeModuleItemId });
            const moduleItem = this.playlist.allModuleItems[currentModuleItemIndex];
            await this.markModuleItemAttemptComplete(moduleItem.id);
            if (
              currentModuleItemIndex === lastModuleItemIndex
              || this.isCompleted
            ) {
              this.snackbarAction = false;
            } else {
              this.snackbarAction = true;
            }
            this.toggleSnackbar({
              text: 'Bulb completed!',
              color: 'success',
              action: this.snackbarAction,
              timeout: this.snackbarAction ? -1 : 3000,
            });
            await this.checkIfAllCompleted();
          } else if (message.type === 'playposit__incompletion') {
            await this.getPlaylist({ playlistId: this.playlistId, sectionId: this.sectionId });
            await this.checkIfAllCompleted();
          } else if (message.type === 'playposit__playlist_bulbRetry') {
            const { moduleItemAttemptId } = this.activeModuleItemAttempt;
            this.resetBulb = this.activeModuleItemId;
            const moduleItemAttemptIndex = _.findIndex(this.allModuleItemAttempts, { moduleItemAttemptId });
            const updatedModuleItemAttempts = this.allModuleItemAttempts;
            updatedModuleItemAttempts.splice(moduleItemAttemptIndex, 1);
            this.setAllModuleItemAttempts(updatedModuleItemAttempts);
            const updatedPlaylist = await this.$api.playlist.resetModuleItemAttempt({
              moduleItemAttemptId,
              playlistId: this.playlist.id,
            });
            await this.setPlaylist(updatedPlaylist.data.playlist);
            if (_.get(updatedPlaylist, 'data.playlistAttempt', false)) {
              await this.setPlaylistAttempt(updatedPlaylist.data.playlistAttempt);
            }
          }
        }
      } catch (err) {
        if (
          err.message !== 'Unexpected token o in JSON at position 1'
          || !err.message.includes('is not valid JSON')
        ) {
          dataLayer.errorLog({ msg: `Unable to catch post message. err: ${JSON.stringify(e)}`, location: 'Playlist: playerPostMessageReceived' });
        }
      }
    },
    async checkLMSLoad() { // If playlist has a resourceLinkId but isn't launched from an LMS then bluescreen
      if (
        this.playlist.isLti
        && !this.queryParams.has('resource_link_id')
        && this.user.userType === 'student'
      ) {
        this.newBlueScreenRedirect({
          title: 'Accessing outside LMS',
          subtitle: 'This playlist is only accessible through your LMS.',
          text: 'This playlist was assigned to this section from a LMS.  It can only be accessed through that LMS.  Close this window and try launching from your LMS.',
        });
      }
    },
    async checkPlayistIsGoogleClassroom() {
      if (!this.playlist?.sections.length) return;
      const section = _.find(this.playlist.sections, { id: this.sectionId });
      if (
        _.get(section, 'key[0]', '') === 'g'
        && section.playlistSection.courseWorkId
        && this.user.userType === 'student'
        && !this.playlistAttempt?.studentSubmissionId
      ) {
        const { data: studentSubmissionId } = await this.$api.googleClassroom.fetchGoogleStudentSubmissionId({
          courseWorkId: _.get(section, 'playlistSection.courseWorkId'),
          courseId: _.get(section, 'key', '').substring(1),
          playlistAttemptId: this.playlistAttempt.playlistAttemptId,
        });
        if (studentSubmissionId) this.setStudentSubmissionId(studentSubmissionId);
      }
    },
    compileModuleItemAttempts() {
      const moduleItemsInPlaylist = [];
      if (!_.get(this.playlistAttempt, 'moduleItemAttempts', []).length) return;
      for (const moduleItemAttempt of this.playlistAttempt.moduleItemAttempts) {
        const module = _.find(this.playlist.modules, { id: _.get(moduleItemAttempt, 'moduleItem.moduleId', 0) });
        moduleItemsInPlaylist.push({
          complete: moduleItemAttempt.complete,
          moduleItemAttemptId: moduleItemAttempt.moduleItemAttemptId,
          moduleItemId: moduleItemAttempt.moduleItemId,
          moduleId: moduleItemAttempt?.moduleItem?.moduleId || 'deleted',
          moduleOrder: module?.order,
          order: moduleItemAttempt?.moduleItem?.order,
        });
      }
      const sortedModuleItems = _.sortBy(moduleItemsInPlaylist, ['moduleOrder', 'order', 'completed']);
      const uniqueModuleItems = _.sortedUniqBy(sortedModuleItems, 'moduleItemId');
      this.setAllModuleItemAttempts(uniqueModuleItems);
    },
    compilePlaylistBulbs() {
      const bulbsInPlaylist = [];
      this.setPlaylistHasLMBs(false);
      for (const module of this.playlist.modules) {
        for (const moduleBulb of _.orderBy(module.moduleBulbs, ['order'], ['asc'])) {
          const moduleIndex = _.findIndex(this.playlist.modules, { id: moduleBulb.moduleId });
          bulbsInPlaylist.push({
            bulbId: moduleBulb.itemId,
            name: _.get(moduleBulb, 'bulb.name', 'Deleted Bulb'),
            ...(this.hasScoreThresholdToAdd(moduleBulb) && { scoreThreshold: this.getPlaylistBulbScoreThreshold(moduleBulb) }),
            moduleId: moduleIndex + 1,
            order: moduleBulb.order,
            complete: !!_.get(moduleBulb, 'bulbAttempt.complete', false),
            deleted: _.isNil(moduleBulb.bulb),
            isLMB: !!moduleBulb.bulb.lmbId,
          });
          if (
            !this.playlistHasLMBs
            && moduleBulb.bulb.lmbId
          ) this.setPlaylistHasLMBs(true);
        }
      }
      this.setAllBulbsInPlaylist(bulbsInPlaylist);
    },
    async checkIfAllCompleted() {
      let completed = true;
      /*
      * This is for an edge case where a student has completed a playlistAttempt prior to moduleItemAttempts being
      * pushed.  This would cause the playlist to not be marked complete and could brick playlist by trying to set
      * a moduleItem that didn't exist.
      */
      if (_.get(this.playlistAttempt, 'complete', false) === 1) completed = true;
      else if (this.playlist.allModuleItems.length > this.allModuleItemAttempts.length) completed = false;
      else {
        for (const moduleItem of this.playlist.allModuleItems) {
          const moduleItemCompleted = _.find(this.allModuleItemAttempts, { moduleItemId: moduleItem.id, complete: 1 });
          if (!moduleItemCompleted) {
            completed = false;
            break;
          }
        }
      }
      this.setIsCompleted(completed);
      if (
        completed
        && this.user.userType === 'student'
        && !this.playlistAttempt?.complete
      ) {
        const playlistAttempt = await this.$api.playlist.markComplete({
          playlistId: this.playlist.id,
          sectionId: this.playlist.section_id,
        });
        this.setPlaylistAttempt(playlistAttempt?.data);
      }
    },
    async findFirstModuleItem() {
      /*
      * There is a completed moduleItem or attempt and the playlist is not completed - set active item to the attempt
      * or next item after completed moduleItem
      */
      if (
        this.allModuleItemAttempts.length
        && !this.isCompleted
      ) {
        const startedModuleItem = _.find(this.allModuleItemAttempts, { complete: 0 });
        if (this.resetBulb) { // resetBulb means a bulb in the playlist is being retaken - set it as active
          const moduleItem = _.find(this.playlist.allModuleItems, { itemId: this.resetBulb });
          this.setActiveModuleItem(moduleItem);
        } else if (startedModuleItem  && startedModuleItem.moduleId !== "deleted") { // A moduleItem was started but not finished - set it as active
          const moduleItem = _.find(this.playlist.allModuleItems, { id: startedModuleItem.moduleItemId });
          this.setActiveModuleItem(moduleItem);
        } else { // No started moduleItems - try to set the next moduleItem after last completed moduleItem
          const completeModuleItem = _.findLast(this.allModuleItemAttempts, { complete: 1 });
          const completeModuleItemIndex = _.findIndex(this.playlist.allModuleItems, { id: completeModuleItem.moduleItemId });
          const moduleItem = this.playlist.allModuleItems[completeModuleItemIndex + 1];
          if (moduleItem) this.setActiveModuleItem(moduleItem);
          else { // There isn't a moduleItem next - set first non completed moduleItem as active
            /*
            * This function attempts to find the first non-started moduleItem in a playlist.  If it can't find
            * one then it sets it to the first item in the playlist.
            */
            const unstartedModuleItem = await this.findFirstUnstartedModuleItem();
            this.setActiveModuleItem(unstartedModuleItem);
          }
        }
      } else if (!this.playlist.allModuleItems.length) { // No moduleItems in playlist - set generic path
        const path = `/course/${this.courseHash}/module/0/item/0`;
        if (this.$route.path !== path) {
          this.$router.push({
            name: 'Course Home',
            params: { courseHash: this.courseHash, moduleId: 0, itemId: 0 },
          });
        }
      } else { // No completed bulbs or playlist is complete - set active item to the first moduleItem
        this.setActiveModuleItem(this.playlist.allModuleItems[0]);
      }
    },
    findFirstUnstartedModuleItem() {
      for (const moduleItem of this.playlist.allModuleItems) {
        const unstartedModuleItem = _.find(this.allModuleItemAttempts, { moduleItemId: moduleItem.id });
        if (!unstartedModuleItem) {
          return moduleItem;
        }
      }
      return this.playlist.allModuleItems[0];
    },
    handleLoadNextItem() {
      this.closeSnackbar();
      this.loadNextItem();
    },
    loadDriftApp() {
      // Load Drift widget for FlipEd Playlists and any copies of them
      if (
        [205, 243].includes(this.playlist.copyPlaylistId)
        || [205, 243].includes(this.playlist.id)
      ) {
        drift.load('sc4b8f7p722p');
        drift.identify(`teacher-${this.user.id}`, {
          playposit_email: this.user.email,
          name: `${this.user.firstName}`,
          user_id: this.user.id,
          user_type: this.user.userType,
          playlistId: this.playlistId,
          sectionId: this.sectionId,
        });
      }
    },
    loadNextItem() {
      // Index of the last moduleItem in the Playlist
      const lastModuleItemIndex = this.playlist.allModuleItems.length - 1;
      // Index of the current active moduleItem
      const currentModuleItemIndex = _.findIndex(this.playlist.allModuleItems, { itemId: this.activeModuleItemId });
      if (currentModuleItemIndex !== lastModuleItemIndex) {
        const nextModuleItemIndex = currentModuleItemIndex + 1;
        const nextModuleItem = this.playlist.allModuleItems[nextModuleItemIndex];
        this.setActiveModuleItem(nextModuleItem);
      }
    },
    newBlueScreenRedirect(props) {
      this.$enablePlaypositErrorScreen({
        vertical: true,
        active_color: '#2196F3',
        base_color: '#0d47a1',
        inactive_color: '#757575',
        title: _.has(props, 'title') ? props.title : 'Error',
        subtitle: _.has(props, 'subtitle') ? props.subtitle : 'Sorry, something has gone wrong.',
        code: _.has(props, 'code') ? props.code : '',
        text: _.has(props, 'text') ? props.text : '',
        icon: _.has(props, 'icon') ? props.icon : 'error',
        buttons: _.has(props, 'buttons') ? props.buttons : [],
      });
      dataLayer.errorLog(props);
    },
    pushRoute({
      courseHash,
      itemId,
      moduleId,
      path,
    }) {
      if (this.$route.path !== path) {
        this.$router.push({
          name: 'Course Home',
          params: { courseHash, moduleId, itemId: itemId || 0 },
        });
      }
    },
    async setActiveModuleItem(moduleItem) {
      // path = the update URL for the provided moduleItem
      const path = `/course/${this.courseHash}/module/${moduleItem.moduleId}/item/${moduleItem.itemId}`;
      this.setModuleItem(moduleItem);
      if (moduleItem.type === 'bulb') {
        if (!_.get(moduleItem, 'bulb')) this.handleLoadNextItem();
        else {
          let lmbBulbId;
          if (_.get(moduleItem, 'bulb.lmbId', false)) {
            let teacherToken;
            if (this.user.userType !== 'teacher') {
              // eslint-disable-next-line
              ({ data: { lmbBulbId, teacherToken } } = await this.getOrCreateLMB({
                bulbId: moduleItem.itemId,
                sectionId: this.sectionId,
              }));
            }
            this.setActiveLMB({
              bulbId: lmbBulbId || moduleItem.itemId,
              name: _.get(moduleItem, 'bulb.name', 'Deleted Bulb'),
              moduleId: moduleItem.moduleId,
              config: _.get(moduleItem, 'config', {}),
              teacherToken,
            });
          } else {
            this.setActivePlaylistVideo({
              id: moduleItem.itemId,
              name: _.get(moduleItem, 'bulb.name', 'Deleted Bulb'),
              ...(this.hasScoreThresholdToAdd(moduleItem) && { scoreThreshold: this.getPlaylistBulbScoreThreshold(moduleItem) }),
              moduleId: moduleItem.moduleId,
              config: _.get(moduleItem, 'config', {}),
            });
          }
          this.pushRoute({
            path,
            courseHash: this.courseHash,
            moduleId: moduleItem.moduleId,
            itemId: _.get(moduleItem, 'config.lmb', false) ? lmbBulbId : moduleItem.itemId,
          });
        }
      } else if (moduleItem.type === 'text') {
        this.setActiveTextElement(moduleItem.text);
        this.pushRoute({
          path,
          courseHash: this.courseHash,
          moduleId: moduleItem.moduleId,
          itemId: moduleItem.itemId,
        });
      } else if (moduleItem.type === 'link') {
        this.setActiveResourceLinkURL(moduleItem);
        this.pushRoute({
          path,
          courseHash: this.courseHash,
          moduleId: moduleItem.moduleId,
          itemId: moduleItem.itemId,
        });
      } else if (_.get(moduleItem, 'attachment.type', '').includes('pdf') || _.get(moduleItem, 'attachment.type', '').includes('image')) {
        this.setActiveResourceLinkURL(moduleItem.attachment.url);
        this.pushRoute({
          path,
          courseHash: this.courseHash,
          moduleId: moduleItem.moduleId,
          itemId: moduleItem.itemId,
        });
      } else {
        this.setActiveModuleItemId(moduleItem.itemId);
        this.setDownloadResourceURL(`${moduleItem.attachment.url}?bcdn_filename=${encodeURIComponent(moduleItem.name)}`);
        this.setNextItemTimeoutToast(null);
        this.pushRoute({
          path,
          courseHash: this.courseHash,
          moduleId: moduleItem.moduleId,
          itemId: moduleItem.itemId,
        });
      }
    },
    setToken() {
      this.setPlaylistId(this.$route.params.courseHash);
      // If there is a token in params - set it
      if (this.queryParams.has('token')) {
        this.setUserToken(this.queryParams.get('token'));
        store.set('pp_module_token', this.queryParams.get('token'));
      } else if (store.get('pp_module_token')) {
        // If token is not in params - check store for token
        this.setUserToken(store.get('pp_module_token'));
      } else {
        // If no token param or store param - blue screen
        this.newBlueScreenRedirect({
          title: 'Unauthorized Access',
          subtitle: 'You are missing a valid token',
          text: 'Please close this window and access the course from the Manage Playlist button in your Dash.',
          status: 401,
          location: 'Playlist.vue',
        });
      }
    },
    setupBulbCompletedEventListener() {
      // Listen for player post message on bulb completion
      window.addEventListener('message', this.playerPostMessageReceived);
    },
    getPlaylistBulbScoreThreshold(moduleItem = {}) {
      return moduleItem.config?.scoreThreshold ?? moduleItem?.bulb?.config?.scoreThreshold;
    },
    hasScoreThresholdToAdd(moduleBulb = {}) {
      return (
        !moduleBulb.config?.clearedThresholdScore
        && moduleBulb.config?.scoreThreshold
      );
    },
  },
  watch: {
    playlist() {
      this.compilePlaylistBulbs();
    },
    async playlistAttempt() {
      await this.compileModuleItemAttempts();
      await this.checkIfAllCompleted();
      if (this.resetBulb) {
        await this.findFirstModuleItem();
        this.resetBulb = null;
      }
    },
    editMode(n, o) {
      if (!n && o) this.findFirstModuleItem();
    },
    isLoaded(n) {
      if (n) {
        this.loadDriftApp();
        this.checkPlayistIsGoogleClassroom();
      }
    },
    snackbar() {
      if (
        this.snackbar
        && this.snackbar.text === 'Bulb completed!'
      ) {
        this.$nextTick(() => {
          const toast = this.$refs.snackbarButton;
          if (toast) toast.$el.focus();
        });
      }
    },
  },
  async created() {
    if (this.$route.name === '404') {
      this.newBlueScreenRedirect({
        title: '404',
        subtitle: 'That page can\'t be found',
        text: 'Check the URL you entered or try launching your course from your user dash.',
        status: 404,
        location: `/course/${window.location.pathname}`,
      });
    } else if (
      this.$route.name === 'Wellbeing'
      && !this.$route.params.courseHash
    ) {
      /*
      * Wellbeing route is missing a courseHash - Redirect to original playlist
      * This will allow us to support older wellbeing playlist links.  All future wellbeing links should include a
      * course has in the URL.
      */
      this.$router.push({
        name: 'Wellbeing',
        params: {
          courseHash: 'm8465exkrj44',
        },
      });
      this.setIsLoaded(true);
    } else if (
      this.$route.params.certificateHash
      || this.$route.name === 'Wellbeing'
    ) {
      this.setIsLoaded(true);
    } else {
      this.setupBulbCompletedEventListener();
      this.setQueryParams(new URLSearchParams(window.location.search));
      this.setToken();
      if (this.userToken) {
        await this.$api.init({
          token: this.userToken,
        });
        try {
          const playlistData = await this.getPlaylist({ playlistId: this.playlistId, sectionId: this.sectionId });
          if (_.get(playlistData, 'playlist.config.nsf', false)) this.setNsf(true);
          if (_.isNil(this.sectionId)) this.setSectionId(playlistData?.playlist?.section_id);
          if (this.queryParams.has('resource_link_id')) this.setResourceLinkId(this.queryParams.get('resource_link_id'));
          this.setIsLti(
            (
              this.playlist.isLti
              || ['true', '1'].includes(this.queryParams.get('lms_launch'))
            ) // eslint-disable-line
          );
          this.setLti13Launch(Boolean(this?.queryParams?.get('lti13Launch')));
          this.setLmsConsumerVersion(this.lmsConsumerVersion);
          this.setLmsIssuer(this?.queryParams?.get('issuer'));
          await this.checkLMSLoad();
          this.$injectCssFile(_.get(this, 'playlist.school.cssInjection', ''));
          await this.checkIfAllCompleted();
          this.findFirstModuleItem();
          this.setIsLoaded(true);
        } catch(e) {
          if (e.blueScreen) {
            this.newBlueScreenRedirect(e.blueScreen);
          } else {
            this.newBlueScreenRedirect({
              title: 'Unable to Retrieve Course',
              subtitle: e.message,
              text: 'Trouble retrieving course.',
            });
          }
        }
      }
    }
    window.PP.Playlist = {
      vm: this,
    };
  },
};
</script>

<style>
  /* ghost is the class added to draggable items when being dragged */
  .ghost {
    border-top: 2px solid #2196F3;
  }

  .playlist-headline {
    font-size: 1.5rem;
    letter-spacing: normal;
  }
</style>
